import { useMemo, useState, FC } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from '@chakra-ui/react';
import { GroupBase, Select } from 'chakra-react-select';
import clsx from 'clsx';
import {
  CLASSNAME_PREFIX,
  MAX_MENU_HEIGHT_PIXELS,
} from 'app/components/Core/ChakraAutocomplete/constants';
import { ChakraSelectProps } from 'app/components/Core/ChakraSelect/types';
import { getStyles } from './styles';
import * as overriddenComponents from './overrides';
import { GroupHeading } from '../ChakraAutocomplete/overrides/GroupHeading';
import { SelectOption } from './types';

export const ChakraSelect: FC<ChakraSelectProps> = ({
  label,
  options,
  name,
  helperText,
  error,
  isClearable = false,
  onChange,
  onBlur,
  value: valueProp,
  mainRef,
  components,
  labelProps,
  isDisabled,
  chakraStyles,
  ...rest
}) => {
  const chackraStyles = useMemo(() => getStyles(chakraStyles), [chakraStyles]);
  const [value, setValue] = useState<SelectOption | null>(valueProp || null);

  const handleOnChange = (option: SelectOption | null) => {
    setValue(option);
    if (onChange) {
      onChange(option);
    }
  };

  return (
    <FormControl variant="floating" isInvalid={Boolean(error)}>
      <Select<SelectOption, false, GroupBase<SelectOption>>
        size="lg"
        name={name}
        label={label}
        value={value}
        onBlur={onBlur}
        options={options}
        onChange={handleOnChange}
        placeholder={null}
        isSearchable={false}
        blurInputOnSelect
        chakraStyles={chackraStyles}
        components={{ ...overriddenComponents, ...components }}
        isClearable={isClearable}
        menuPlacement="auto"
        classNamePrefix={CLASSNAME_PREFIX}
        maxMenuHeight={MAX_MENU_HEIGHT_PIXELS}
        menuShouldScrollIntoView={false}
        setOpen={() => {}}
        setInputValue={() => {}}
        getOptionLabel={(option) => option.title}
        getOptionValue={(option) => option.value.toString()}
        formatOptionLabel={(option) => option.title}
        isDisabled={isDisabled}
        ref={mainRef}
        formatGroupLabel={GroupHeading}
        {...rest}
      />
      <FormLabel
        htmlFor={name}
        {...labelProps}
        className={clsx(labelProps?.className, {
          isDisabled: Boolean(isDisabled),
        })}
      >
        {label}
      </FormLabel>
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
      {helperText && error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
  );
};
